import React from 'react';
import { JsonLd } from 'react-schemaorg';

const SchemaMarkup = () => {
  const baseUrl = 'https://martingale-strategy-calc.vercel.app';

  // WebSite schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'Martingale Strategy Calculator',
    'url': `${baseUrl}/`,
    'description': 'Free online Martingale betting calculator for roulette and casino games. Calculate progression tables, bankroll requirements, risk of ruin, and test the martingale system.'
  };

  // WebApplication / SoftwareApplication schema
  const softwareApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    'name': 'Martingale Strategy Calculator & Roulette Simulator',
    'operatingSystem': 'All',
    'applicationCategory': 'FinanceApplication',
    'browserRequirements': 'Requires JavaScript. Requires HTML5.',
    'offers': {
      '@type': 'Offer',
      'price': '0.00',
      'priceCurrency': 'USD'
    },
    'url': `${baseUrl}/`,
    'description': 'Free online Martingale calculator to simulate betting progressions, manage bankroll, assess risk of ruin, and test roulette strategies.',
    'featureList': [
      'Martingale and Grand Martingale Progression Table',
      'Multi-Format Odds Converter (Decimal, Fractional, American, Implied Probability)',
      'Bankroll Risk Assessment and Consecutive Loss Analysis',
      'Monte Carlo Progression Simulator',
      'Table Limits and House Edge Calculations'
    ],
    'screenshot': `${baseUrl}/logo512.png`
  };

  // FAQPage schema for high-ranking SERP rich snippets
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'Is the Martingale system safe?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'The Martingale betting strategy carries significant risk. While it can produce short-term wins, it requires an unlimited bankroll and no table limits to guarantee long-term success. Our Martingale calculator helps you understand and visualize these risks before placing bets.'
        }
      },
      {
        '@type': 'Question',
        'name': 'What is the best Martingale strategy?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'The best Martingale strategy depends on your bankroll, risk tolerance, and game odds. Testing smaller base bets (e.g. 1-2% of bankroll) gives more room for recovery before hitting table limits or exhausting your funds.'
        }
      },
      {
        '@type': 'Question',
        'name': 'How does the Martingale calculator work?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Our Martingale betting calculator simulates progressive betting rounds based on your inputs: initial base bet, bankroll, odds, and rounds. It calculates required bets per loss streak, cumulative amount wagered, net profit per win, and probability of consecutive losses.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Can I use this for roulette strategies?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Yes! The Martingale calculator is widely used for roulette strategies on even-money outside bets like Red/Black, Odd/Even, and High/Low (1-18/19-36). The tool factors in European (2.7% house edge) and American (5.26% house edge) roulette dynamics.'
        }
      },
      {
        '@type': 'Question',
        'name': 'What bankroll do I need for Martingale?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'The required bankroll depends on your base bet and the loss streak you want to survive. For an initial bet of $10, surviving 6 consecutive losses requires $630 ($10 + $20 + $40 + $80 + $160 + $320), while surviving 8 losses requires $2,550.'
        }
      }
    ]
  };

  // HowTo schema
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': 'How to Use the Martingale Strategy Calculator',
    'description': 'Step-by-step guide to calculating betting progressions and bankroll requirements with the Martingale Strategy Calculator.',
    'step': [
      {
        '@type': 'HowToStep',
        'position': 1,
        'name': 'Enter Base Bet and Bankroll',
        'text': 'Specify your starting base bet (e.g., $10) and your total available session bankroll.'
      },
      {
        '@type': 'HowToStep',
        'position': 2,
        'name': 'Set Odds and Multiplier',
        'text': 'Enter the game payout odds (e.g., 2.00 for even-money roulette) and progression multiplier (default is 2x).'
      },
      {
        '@type': 'HowToStep',
        'position': 3,
        'name': 'Analyze Progression and Risk',
        'text': 'Review the calculated loss progression table, cumulative bet totals, consecutive loss probability, and Monte Carlo simulation.'
      }
    ]
  };

  return (
    <>
      <JsonLd item={websiteSchema} />
      <JsonLd item={softwareApplicationSchema} />
      <JsonLd item={faqSchema} />
      <JsonLd item={howToSchema} />
    </>
  );
};

export default SchemaMarkup;